/**
 * The shared vocabulary: brand lockup, buttons, tags, meter, stars, counter.
 * These render the same class names the stylesheet already defines, so the
 * design system carried over from the pre-React build unchanged.
 */
import {
  Fragment,
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type AnchorHTMLAttributes,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { motion } from 'motion/react';
import { springSoft, staggered, prefersReducedMotion } from '../lib/motion';
import { useStrings } from '../state/useLocale';
import { COURSE_HREF } from '../lib/landing';

/* ── Brand ───────────────────────────────────────────────────────────────── */

export interface MarkProps {
  className?: string;
  /**
   * Draw the rounded tile behind the glyphs, as the 32-unit master does. Off
   * by default: in a bar the mark sits straight on the ground. The boot
   * screen turns it on, because its sequence resolves structure first and the
   * tile is the structure.
   */
  tile?: boolean;
  /** The caret blinks like a hardware cursor. The one thing allowed to animate forever (motion.css §2). */
  live?: boolean;
}

/**
 * The mark: a prompt chevron and a block caret.
 *
 * The caret is the only part that carries Signal Green, coloured from
 * `brand.css` through the `caret` class rather than here, so the brand layer
 * stays removable in one line. Coordinates are for a 24-unit box; the 32-unit
 * master used as the favicon lives at web/public/mark.svg.
 *
 * Each part carries the class motion.css addresses — `tile`, `chevron`,
 * `caret` — so the boot sequence can resolve them in order.
 */
export function Mark({ className = 'brand-mark', tile = false, live = false }: MarkProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      {tile && <rect className="tile" x="0" y="0" width="24" height="24" rx="5.25" />}
      <path
        className="chevron"
        d="M7 8.5L11.5 12L7 15.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect className={live ? 'caret is-live' : 'caret'} x="14.5" y="7.5" width="4" height="9" fill="currentColor" />
    </svg>
  );
}

/**
 * The lockup: the mark, and the wordmark beside it.
 *
 * The wordmark is set in Latin in both languages and is not in the
 * catalogue. It is the product's name — the thing on the favicon, in the
 * address bar and on the invoice — and a name is not translated
 * (docs/i18n.md §2). The `aria-label` around it is ordinary prose and is.
 */
/**
 * The mark, and the word beside it unless the caller asked for the small one.
 *
 * `href` defaults to the ledger, which is where "home" means inside the app.
 * It used to be `?` — the query string emptied — and that stopped being the
 * level list the day `/` became the landing page for everybody: a bare `?`
 * would still render the ledger while the tab lived, and hand back the pitch
 * the moment anybody reloaded or bookmarked it (lib/landing.ts).
 *
 * The two pages outside the app pass their own: the public profile at
 * `/u/<tag>`, where `?` was never home to begin with, and the landing page,
 * whose mark is the page the reader is already on rather than a way into a
 * course they have not asked for yet.
 */
export function Brand({ compact = false, title, live = false, href = COURSE_HREF }: { compact?: boolean; title?: string; live?: boolean; href?: string }) {
  const s = useStrings().app.ui;
  return (
    <a className={compact ? 'brand brand-sm' : 'brand'} href={href} title={title} aria-label={s.brandHome}>
      <Mark live={live} />
      {!compact && (
        <span className="brand-word" dir="ltr">
          Engineer<b>OS</b>
        </span>
      )}
    </a>
  );
}

/* ── Typed ───────────────────────────────────────────────────────────────── */

/**
 * A command the product types on the learner's behalf (motion.css §3): for
 * onboarding, empty states and demo replays. Never for output the learner is
 * waiting on. The character count drives both the duration and the width, so
 * the text must be one line of a monospace face.
 */
export function Typed({ text, className, delay = 0 }: { text: string; className?: string; delay?: number }) {
  const style: CSSProperties & { '--ch': number } = { '--ch': text.length };
  if (delay > 0) style.animationDelay = `${delay}ms`;
  return (
    <span className={className ? `typed ${className}` : 'typed'} style={style}>
      {text}
    </span>
  );
}

/* ── Buttons ─────────────────────────────────────────────────────────────── */

type Variant = 'solid' | 'quiet' | 'plain';

const btnClass = (variant: Variant, size?: 'lg', wide?: boolean, extra?: string) =>
  [
    'btn',
    variant === 'solid' ? 'btn-solid' : variant === 'quiet' ? 'btn-quiet' : '',
    size === 'lg' ? 'btn-lg' : '',
    wide ? 'btn-wide' : '',
    extra ?? '',
  ]
    .filter(Boolean)
    .join(' ');

export function Button({
  variant = 'plain',
  size,
  wide,
  className,
  children,
  ...rest
}: { variant?: Variant; size?: 'lg'; wide?: boolean; children: ReactNode } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button className={btnClass(variant, size, wide, className)} {...rest}>
      {children}
    </button>
  );
}

export function LinkButton({
  variant = 'plain',
  size,
  wide,
  className,
  children,
  ...rest
}: { variant?: Variant; size?: 'lg'; wide?: boolean; children: ReactNode } & AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <a className={btnClass(variant, size, wide, className)} {...rest}>
      {children}
    </a>
  );
}

/* ── Tag ─────────────────────────────────────────────────────────────────── */

export type TagTone = 'plain' | 'on' | 'alert' | 'off';

export function Tag({ tone = 'plain', children }: { tone?: TagTone; children: ReactNode }) {
  const cls = ['tag', tone === 'on' ? 'tag-on' : tone === 'alert' ? 'tag-alert' : tone === 'off' ? 'tag-off' : '']
    .filter(Boolean)
    .join(' ');
  return <span className={cls}>{children}</span>;
}

/* ── Meter ───────────────────────────────────────────────────────────────── */

const clampPct = (n: number) => Math.max(0, Math.min(100, Math.round(n)));

/**
 * The rank meter.
 *
 * Plain width, with the stylesheet's own transition for ordinary changes.
 * Pass `from` for the one change that is an event, a rank advancing: the fill
 * then ticks from there to `pct` in eight discrete segments (motion.css §6),
 * because the number underneath it is discrete. The fill is keyed on the
 * pair, so a second advance replays rather than resumes.
 */
export function Meter({
  pct,
  from,
  delay = 0,
  label,
}: {
  pct: number;
  from?: number;
  /** Milliseconds before an advance starts ticking, so it can wait its turn behind a count or a star. */
  delay?: number;
  label?: string;
}) {
  const s = useStrings().app.ui;
  const to = clampPct(pct);
  const start = from === undefined ? undefined : clampPct(from);
  const advancing = start !== undefined && start !== to;
  const style: CSSProperties & { '--pct'?: string; '--pct-from'?: string } = { width: `${to}%` };
  if (advancing) {
    style['--pct-from'] = `${start}%`;
    style['--pct'] = `${to}%`;
    // Inline, so it wins over the kit's shorthand; the kit's reduced-motion
    // rule is `!important` and still zeroes it for a viewer who asked.
    if (delay > 0) style.animationDelay = `${delay}ms`;
  }
  return (
    <div
      className={advancing ? 'meter is-advancing' : 'meter'}
      role="progressbar"
      aria-label={label ?? s.progress}
      aria-valuenow={to}
    >
      <div key={advancing ? `${start}-${to}` : 'still'} style={style} />
    </div>
  );
}

/* ── Stars ───────────────────────────────────────────────────────────────── */

export function Stars({
  value,
  className,
  animateIn = false,
  onLand,
}: {
  value: number;
  className?: string;
  /** Land each earned star in turn, for the level-clear moment. */
  animateIn?: boolean;
  onLand?: (index: number) => void;
}) {
  const s = useStrings().app.ui;
  const fired = useRef(false);
  useEffect(() => {
    if (!animateIn || !onLand || fired.current) return;
    fired.current = true;
    const reduced = prefersReducedMotion();
    const timers = [0, 1, 2]
      .filter((i) => i < value)
      .map((i) => window.setTimeout(() => onLand(i), reduced ? 0 : 260 + i * 190));
    return () => timers.forEach(clearTimeout);
  }, [animateIn, onLand, value]);

  return (
    <span className={className ? `stars ${className}` : 'stars'} aria-label={s.stars(value)}>
      {[1, 2, 3].map((i) =>
        animateIn ? (
          <motion.span
            key={i}
            className={i <= value ? 'on' : ''}
            initial={{ scale: 0.3, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={staggered(i - 1, 0.19, springSoft)}
          >
            ★
          </motion.span>
        ) : (
          <span key={i} className={i <= value ? 'on' : ''}>
            ★
          </span>
        ),
      )}
    </span>
  );
}

/* ── Counter ─────────────────────────────────────────────────────────────── */

/**
 * A number that counts to its new value instead of jumping.
 *
 * It starts at zero rather than at `value`, because the interesting case is a
 * number that is already final when it mounts: the XP on the level-clear panel
 * never changes for the life of that panel, so seeding from the prop would
 * skip the animation entirely and the figure would simply appear.
 */
export function Counter({ value, duration = 700 }: { value: number; duration?: number }) {
  const [shown, setShown] = useState(0);
  const from = useRef(0);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setShown(value);
      from.current = value;
      return;
    }
    const start = performance.now();
    const a = from.current;
    const b = value;
    if (a === b) return;
    let raf = 0;
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      // easeOutCubic: fast then settling, which reads as counting up.
      const eased = 1 - Math.pow(1 - t, 3);
      setShown(Math.round(a + (b - a) * eased));
      if (t < 1) raf = requestAnimationFrame(step);
      else from.current = b;
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);

  return <>{shown}</>;
}

/* ── Language ────────────────────────────────────────────────────────────── */

/**
 * A sentence that has something other than words in it.
 *
 * A few sentences in the product carry an element mid-clause — a star, a
 * piece of bold, a level name. They cannot be plain strings, and they must
 * not be assembled at the call site either: the order of the pieces is part
 * of the translation, and a component that writes `{before}{stars}{after}`
 * has decided that order for every language at once.
 *
 * So the catalogue returns the pieces as an array and this renders them.
 * Index keys are correct here and nowhere else: the array is a sentence, it
 * is rebuilt whole whenever it changes, and its elements never reorder
 * relative to each other within one language.
 */
export function Parts({ of }: { of: ReactNode[] }) {
  return (
    <>
      {of.map((part, i) => (
        <Fragment key={i}>{part}</Fragment>
      ))}
    </>
  );
}

/**
 * A machine string inside prose.
 *
 * A path, a branch name or a hash is left-to-right text, and inside an Arabic
 * sentence the bidi algorithm will reorder anything it thinks is punctuation
 * at the edges — `src/cart.js` can arrive as `cart.js/src`, which looks like
 * the level lying rather than the renderer. `dir="ltr"` on an element says
 * "this run is its own direction", and `unicode-bidi: isolate` (base.css)
 * stops it from disturbing the words either side of it.
 *
 * Use it for anything the machine wrote that is not already inside a `<code>`
 * or a `<pre>`, both of which get the same treatment from the stylesheet.
 */
export function Machine({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={className ? `machine ${className}` : 'machine'} dir="ltr">
      {children}
    </span>
  );
}
